import type { AxiosInstance } from 'axios'
import type { IFactoryFunctionRequest, paginate } from '@bc-library/interface'
import { EMethod } from '@bc-library/interface'
import { API_MAIN_VERSION, BASE_URL } from '@/config/constantes'
import useAxios from '@/services/api/useAxios'

const resource = 'tickets'

// Types
export type TicketStatus = 'OPEN' | 'ONGOING' | 'WAITING' | 'ARCHIVED' | 'DONE'
export const ACTIVE_TICKET_STATUSES: TicketStatus[] = ['OPEN', 'ONGOING', 'WAITING', 'DONE']
export type TicketPriority = 1 | 2 | 3

export type TicketRequestType = 'BUG' | 'FEATURE' | 'QUESTION'

export const TICKET_REQUEST_TYPES: TicketRequestType[] = ['BUG', 'FEATURE', 'QUESTION']

export function toTicketStatus(value: unknown): TicketStatus {
  if (value === 'OPEN' || value === 'ONGOING' || value === 'WAITING' || value === 'ARCHIVED' || value === 'DONE')
    return value
  return 'OPEN'
}

export function toTicketPriority(value: unknown): TicketPriority {
  if (value === 1 || value === 2 || value === 3)
    return value
  return 2
}

export function toTicketRequestType(value: unknown): TicketRequestType | null {
  if (value === 'BUG' || value === 'FEATURE' || value === 'QUESTION')
    return value
  return null
}

export type ModuleIdentifier =
  | 'HR'
  | 'ADMIN-USER'
  | 'ADMIN-DEGREES'
  | 'ADMIN-COMPANIES'
  | 'ADMIN-JOBS'
  | 'ADMIN-ORGANIZATION'
  | 'ADMIN-SKILLS'
  | 'RECRUITMENT'
  | 'PROJECT'
  | 'LEAVE'
  | 'EVALUATIONS'
  | 'SALESFUNNEL'
  | 'TRAININGS'
  | 'SURVEYS'
  | 'EXPORT'
  | 'INVOICES'
  | 'TIMESHEET'
  | 'DASHBOARD'
  | 'EXPENSES'

export interface ResourceSummary {
  _id: string
  firstname: string
  lastname: string
  trigram: string
}

export interface OfficeSummary {
  _id: string
  display: string
  countryId?: string
}

export interface LinkedFile {
  _id: string
  _created: Date
  authorId: string
  filePath: string
  fileName: string
  fileType: string
  fileSize: number
}

export interface StatusHistory {
  _id: string
  _created: Date
  resourceId: string
  status: TicketStatus
  resource?: ResourceSummary
}

export interface Message {
  _id: string
  _created: Date
  message: string
  authorId: string
  taggedIds: string[]
  taggedAdmins?: Array<{ name: string, email: string }>
  linkedFiles: LinkedFile[]
  author?: ResourceSummary
  tagged?: ResourceSummary[]
}

export interface Ticket {
  _id: string
  _created: Date
  _firmId: string
  deletedAt: Date | null
  isDeleted: boolean
  display: string
  description: string
  priority: TicketPriority
  deadline: Date | null
  officeId: string | null
  authorId: string
  managedByIds: string[]
  moduleIdentifier: ModuleIdentifier | null
  requestType: TicketRequestType | null
  messages: Message[]
  history: StatusHistory[]
  currentStatus: StatusHistory
  checklist?: ChecklistItem[]
  author?: ResourceSummary
  office?: OfficeSummary
  managedBy?: ResourceSummary[]
}

export interface TicketListItem {
  _id: string
  _created: Date
  display: string
  description: string
  priority: TicketPriority
  deadline: Date | null
  moduleIdentifier: ModuleIdentifier | null
  requestType: TicketRequestType | null
  currentStatus: StatusHistory
  messagesCount: number
  author: ResourceSummary
  office: OfficeSummary | null
  managedBy: ResourceSummary[]
}

export interface BoardTicket {
  _id: string
  display: string
  priority: TicketPriority
  deadline: Date | null
  authorId: string
  managedByIds: string[]
  requestType: TicketRequestType | null
  messagesCount: number
  _created: Date
  office: OfficeSummary | null
}

export interface BoardColumn {
  tickets: BoardTicket[]
  count: number
}

export interface BoardResponse {
  OPEN: BoardColumn
  ONGOING: BoardColumn
  WAITING: BoardColumn
  DONE: BoardColumn
  ARCHIVED?: BoardColumn
}

export interface CreateTicketRequest {
  ticket: {
    display: string
    description?: string
    priority?: TicketPriority
    deadline?: Date | null
    officeId?: string | null
    managedByIds?: string[]
    moduleIdentifier?: ModuleIdentifier | null
    requestType: TicketRequestType
    initialMessage?: string
  }
  files?: FileUpload[]
  checklist?: ChecklistItem[]
  bypassDuplicateCheck?: boolean
}

export interface UpdateTicketRequest {
  ticket: {
    display?: string
    description?: string
    priority?: TicketPriority
    deadline?: Date | null
    officeId?: string | null
    managedByIds?: string[]
    moduleIdentifier?: ModuleIdentifier | null
    requestType?: TicketRequestType | null
  }
  checklist?: ChecklistItem[]
}

export interface FileUpload {
  base64: string
  fileName: string
  fileType: string
  fileSize: number
}

export interface ChecklistItem {
  _id?: string
  label: string
  checked: boolean
}

export interface AddMessageRequest {
  message: string
  taggedIds?: string[]
  taggedAdmins?: Array<{ name: string; email: string }>
  files?: FileUpload[]
}

// Sort options
export type TicketSortBy = 'custom' | 'priority' | 'priority-recent' | 'priority-oldest' | 'created' | 'deadline'

// Reorder request
export interface ReorderTicketsRequest {
  tickets: Array<{ _id: string; order: number }>
}

// Move request
export interface MoveTicketRequest {
  status?: TicketStatus
  order?: number
}

// API functions
const filter = (axios: IFactoryFunctionRequest<paginate<TicketListItem>, undefined>) => (query: {
  page?: number
  limit?: number
  search?: string
  status?: TicketStatus
  priority?: number[]
  moduleIdentifier?: string[]
  managedByIds?: string[]
  authorIds?: string[]
  requestType?: TicketRequestType[]
  officeId?: string
  sortBy?: TicketSortBy
}) => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2`,
    query,
  })
}

const getBoard = (axios: IFactoryFunctionRequest<BoardResponse, undefined>) => (query: {
  search?: string
  moduleIdentifier?: string[]
  managedByIds?: string[]
  authorIds?: string[]
  requestType?: TicketRequestType[]
  officeId?: string
  priority?: number[]
  sortBy?: TicketSortBy
}) => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/board`,
    query,
  })
}

const getById = (axios: IFactoryFunctionRequest<Ticket, undefined>) => (id: string) => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${id}`,
  })
}

const create = (axios: IFactoryFunctionRequest<Ticket, CreateTicketRequest>) => (data: CreateTicketRequest) => {
  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2`,
    data,
  })
}

const update = (axios: IFactoryFunctionRequest<Ticket, UpdateTicketRequest>) => (id: string, data: UpdateTicketRequest) => {
  return axios({
    method: EMethod.patch,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${id}`,
    data,
  })
}

const updateStatus = (axios: IFactoryFunctionRequest<Ticket, { status: TicketStatus }>) => (id: string, status: TicketStatus) => {
  return axios({
    method: EMethod.patch,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${id}/status`,
    data: { status },
  })
}

const addMessage = (axios: IFactoryFunctionRequest<Ticket, AddMessageRequest>) => (id: string, data: AddMessageRequest) => {
  return axios({
    method: EMethod.patch,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${id}/message`,
    data,
  })
}

const deleteTicket = (axios: IFactoryFunctionRequest<void, undefined>) => (id: string) => {
  return axios({
    method: EMethod.delete,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${id}`,
  })
}

const deleteMessage = (axios: IFactoryFunctionRequest<Ticket, undefined>) => (ticketId: string, messageId: string) => {
  return axios({
    method: EMethod.delete,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${ticketId}/message/${messageId}`,
  })
}

const editMessage = (axios: IFactoryFunctionRequest<Ticket, { message?: string; taggedIds?: string[]; taggedAdmins?: Array<{ name: string; email: string }>; files?: FileUpload[] }>) => (
  ticketId: string,
  messageId: string,
  data: { message?: string; taggedIds?: string[]; taggedAdmins?: Array<{ name: string; email: string }>; files?: FileUpload[] }
) => {
  return axios({
    method: EMethod.patch,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${ticketId}/message/${messageId}`,
    data,
  })
}

const deleteFile = (axios: IFactoryFunctionRequest<Ticket, undefined>) => (
  ticketId: string,
  messageId: string,
  fileId: string
) => {
  return axios({
    method: EMethod.delete,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${ticketId}/message/${messageId}/file/${fileId}`,
  })
}

// Activity types
export interface Activity {
  type: 'message' | 'status_change' | 'ticket_created'
  _created: Date
  authorId: string | null
  ticketId: string
  ticketDisplay: string
  preview?: string
  status?: TicketStatus
  author?: ResourceSummary
  isAdminMessage?: boolean
  isAdminAction?: boolean
  adminAuthor?: { name: string, email: string } | null
  firmId?: string
  firmName?: string
}

export interface ActivityFeedResponse {
  docs: Activity[]
  count: {
    total: number
    currentPage: number
    nextPage: number
    totalPages: number
    hasNextPage: boolean
    limit: number
  }
}

export interface UnseenCountResponse {
  unseenCount: number
}

const getUnseenCount = (axios: IFactoryFunctionRequest<UnseenCountResponse, undefined>) => () => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/unseen-count`,
  })
}

const getActivityFeed = (axios: IFactoryFunctionRequest<ActivityFeedResponse, undefined>) => (query: {
  page?: number
  limit?: number
  types?: string
}) => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/activity-feed`,
    query,
  })
}

const markAsRead = (axios: IFactoryFunctionRequest<{ success: boolean }, undefined>) => (id: string) => {
  return axios({
    method: EMethod.patch,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${id}/mark-read`,
  })
}

const markAllAsRead = (axios: IFactoryFunctionRequest<{ success: boolean; markedCount: number }, undefined>) => () => {
  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/mark-all-read`,
  })
}

export interface ArchivedTicketListItem extends TicketListItem {
  archivedAt: Date
}

const getArchived = (axios: IFactoryFunctionRequest<paginate<ArchivedTicketListItem>, undefined>) => (query: {
  page?: number
  limit?: number
  search?: string
  moduleIdentifier?: string[]
  managedByIds?: string[]
  officeId?: string
  sortBy?: TicketSortBy
}) => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/archived`,
    query,
  })
}

// Reorder tickets within a status column (custom sort mode)
const reorder = (axios: IFactoryFunctionRequest<{ success: boolean }, ReorderTicketsRequest>) => (data: ReorderTicketsRequest) => {
  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/reorder`,
    data,
  })
}

// Move a ticket (change status and/or order)
const move = (axios: IFactoryFunctionRequest<BoardTicket, MoveTicketRequest>) => (id: string, data: MoveTicketRequest) => {
  return axios({
    method: EMethod.patch,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${id}/move`,
    data,
  })
}

// Analytics types
export type DailyBreakdown = {
  byPriority: Record<number, number>
}

export type DailySeries = {
  date: string
  created: DailyBreakdown
  closed: DailyBreakdown
}

export type ResolutionByPriority = {
  count: number
  avgMs: number
}

export type TicketAnalyticsResponse = {
  year: number
  month: number
  dailySeries: DailySeries[]
  resolution: {
    avgClosedMs: number
    avgOpenMs: number
    closedCount: number
    openCount: number
    byPriority: Record<number, ResolutionByPriority>
  }
}

const getAnalytics = (axios: IFactoryFunctionRequest<TicketAnalyticsResponse, undefined>) => (query?: { year?: number, month?: number }) => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/analytics`,
    query,
  })
}

export default (axios: AxiosInstance) => ({
  filter: filter(useAxios(axios)),
  getBoard: getBoard(useAxios(axios)),
  getById: getById(useAxios(axios)),
  create: create(useAxios(axios)),
  update: update(useAxios(axios)),
  updateStatus: updateStatus(useAxios(axios)),
  addMessage: addMessage(useAxios(axios)),
  deleteTicket: deleteTicket(useAxios(axios)),
  deleteMessage: deleteMessage(useAxios(axios)),
  editMessage: editMessage(useAxios(axios)),
  deleteFile: deleteFile(useAxios(axios)),
  getUnseenCount: getUnseenCount(useAxios(axios)),
  getActivityFeed: getActivityFeed(useAxios(axios)),
  markAsRead: markAsRead(useAxios(axios)),
  markAllAsRead: markAllAsRead(useAxios(axios)),
  getArchived: getArchived(useAxios(axios)),
  reorder: reorder(useAxios(axios)),
  move: move(useAxios(axios)),
  getAnalytics: getAnalytics(useAxios(axios)),
})
